import { base64ToBytes, bytesToBase64, deriveV2KeyBytesFromSignature } from './chatCrypto';
import { normalizeChatWallet } from './chatDeviceIdentity';
export const CHAT_VAULT_VERSION = 1;
const RECOVERY_VERSION = 1;
const encoder = new TextEncoder();
const decoder = new TextDecoder();
function normalizedRoom(room) {
    const result = room.trim().toLowerCase();
    if (!result)
        throw new Error('A room identifier is required');
    return result;
}
function aad(wallet, room, version = CHAT_VAULT_VERSION) {
    return encoder.encode(`dwdata-chat-vault|v=${version}|wallet=${normalizeChatWallet(wallet)}|room=${normalizedRoom(room)}`);
}
function recoveryAad(wallet) {
    return encoder.encode(`dwdata-chat-vault-recovery|v=${RECOVERY_VERSION}|wallet=${normalizeChatWallet(wallet)}`);
}
function deviceAad(wallet, deviceId) {
    return encoder.encode(`dwdata-chat-vault-device|v=${RECOVERY_VERSION}|wallet=${normalizeChatWallet(wallet)}|device=${deviceId}`);
}
function checkedBytes(encoded, label, minLength) {
    try {
        const bytes = base64ToBytes(encoded);
        if (bytes.length < minLength)
            throw new Error('too short');
        return bytes;
    }
    catch {
        throw new Error(`Invalid chat vault ${label}`);
    }
}
async function importAes(raw, usages, extractable = false) {
    if (raw.length !== 32)
        throw new Error('Chat vault key material must be 32 bytes');
    return crypto.subtle.importKey('raw', raw, 'AES-GCM', extractable, usages);
}
async function encryptBytes(key, bytes, additionalData) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData }, key, bytes);
    return { iv: bytesToBase64(iv), ciphertext: bytesToBase64(new Uint8Array(ciphertext)) };
}
async function decryptBytes(key, iv, ciphertext, additionalData) {
    try {
        return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: checkedBytes(iv, 'IV', 12), ...(additionalData ? { additionalData } : {}) }, key, checkedBytes(ciphertext, 'ciphertext', 17)));
    }
    catch {
        // Never leak whether the key, AAD, tag, or ciphertext was wrong.
        throw new Error('Chat vault integrity check failed');
    }
}
export async function createChatVaultMasterKey() {
    // This short-lived creation form is extractable only so callers can create
    // recovery/device records.  All unwrapped operational keys are imported
    // non-extractable below.  The raw bytes are never returned or logged.
    return importAes(crypto.getRandomValues(new Uint8Array(32)), ['encrypt', 'decrypt'], true);
}
/**
 * Conflict-safe first-vault key. Two new devices racing before the first
 * StorJ write derive the same wallet-scoped master without exchanging it.
 * Existing vaults continue to use their wrapped master key.
 */
export async function deriveInitialChatVaultMasterKey(signature, wallet) {
    const v2Bytes = await deriveV2KeyBytesFromSignature(signature);
    const separated = await crypto.subtle.digest('SHA-256', encoder.encode(`dwdata-chat-vault-initial-master-v1|wallet=${normalizeChatWallet(wallet)}|${bytesToBase64(v2Bytes)}`));
    return importAes(new Uint8Array(separated), ['encrypt', 'decrypt'], true);
}
export async function encryptChatVault(plaintext, masterKey, wallet, room) {
    const encrypted = await encryptBytes(masterKey, encoder.encode(plaintext), aad(wallet, room));
    return { v: CHAT_VAULT_VERSION, ...encrypted };
}
export async function decryptChatVault(envelope, masterKey, wallet, room) {
    if (!envelope || envelope.v !== CHAT_VAULT_VERSION || typeof envelope.iv !== 'string' || typeof envelope.ciphertext !== 'string') {
        throw new Error('Unsupported chat vault envelope version');
    }
    return decoder.decode(await decryptBytes(masterKey, envelope.iv, envelope.ciphertext, aad(wallet, room)));
}
/**
 * Explicit migration only.  It accepts the old v0 representation and produces
 * a v1 envelope whose metadata is authenticated; normal decrypt never silently
 * accepts v0 data.
 */
export async function migrateLegacyChatVaultEnvelope(legacy, masterKey, wallet, room) {
    if (!legacy || legacy.v !== 0)
        throw new Error('Unsupported legacy chat vault envelope');
    const plaintext = decoder.decode(await decryptBytes(masterKey, legacy.iv, legacy.ciphertext));
    return encryptChatVault(plaintext, masterKey, wallet, room);
}
/** Separate from the existing v2 derivation so a recovery key can never be a chat wire key. */
export async function deriveChatVaultRecoveryKey(signature) {
    const v2Bytes = await deriveV2KeyBytesFromSignature(signature);
    const separated = await crypto.subtle.digest('SHA-256', encoder.encode(`dwdata-chat-vault-recovery-v1|${bytesToBase64(v2Bytes)}`));
    return importAes(new Uint8Array(separated), ['encrypt', 'decrypt']);
}
export async function wrapVaultKeyForRecovery(masterKey, signature, wallet) {
    const recovery = await deriveChatVaultRecoveryKey(signature);
    const raw = new Uint8Array(await crypto.subtle.exportKey('raw', masterKey));
    const encrypted = await encryptBytes(recovery, raw, recoveryAad(wallet));
    return { v: RECOVERY_VERSION, kind: 'recovery', ...encrypted };
}
export async function unwrapVaultKeyFromRecovery(record, signature, wallet) {
    if (!record || record.v !== RECOVERY_VERSION || record.kind !== 'recovery')
        throw new Error('Unsupported recovery key record');
    const raw = await decryptBytes(await deriveChatVaultRecoveryKey(signature), record.iv, record.ciphertext, recoveryAad(wallet));
    return importAes(raw, ['encrypt', 'decrypt']);
}
async function deviceWrappingKey(identity) {
    const publicKey = await crypto.subtle.importKey('jwk', identity.ecdhPublicJwk, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
    const shared = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, identity.ecdhPrivateKey, 256);
    const material = await crypto.subtle.digest('SHA-256', shared);
    return importAes(new Uint8Array(material), ['encrypt', 'decrypt']);
}
export async function wrapVaultKeyForDevice(masterKey, identity, wallet) {
    if (identity.wallet !== normalizeChatWallet(wallet))
        throw new Error('Device identity wallet mismatch');
    const raw = new Uint8Array(await crypto.subtle.exportKey('raw', masterKey));
    const encrypted = await encryptBytes(await deviceWrappingKey(identity), raw, deviceAad(wallet, identity.deviceId));
    return { v: RECOVERY_VERSION, kind: 'device', deviceId: identity.deviceId, ...encrypted };
}
export async function unwrapVaultKeyFromDevice(record, identity, wallet) {
    if (!record || record.v !== RECOVERY_VERSION || record.kind !== 'device' || record.deviceId !== identity.deviceId) {
        throw new Error('Device key record does not belong to this device');
    }
    if (identity.wallet !== normalizeChatWallet(wallet))
        throw new Error('Device identity wallet mismatch');
    const raw = await decryptBytes(await deviceWrappingKey(identity), record.iv, record.ciphertext, deviceAad(wallet, identity.deviceId));
    return importAes(raw, ['encrypt', 'decrypt']);
}
